import React, { useId } from "react";
import "./TextInput.css";

/**
 * Brand text field — surface-aware (reads --field-* vars, so it adapts to
 * .surface-manager vs .surface-game without per-call styling). Label sits above
 * the input. Generalizes the question flow's AnswerTextField for app-wide use.
 *
 * Props:
 *   label, helperText, error (bool)
 *   leftIcon: optional node rendered inside, left of the input
 *   fullWidth (default true)
 *   multiline: render a <textarea> instead of <input> (e.g. a description).
 *              `rows` and other native textarea props spread via ...rest.
 *   ...rest spreads onto the native <input>/<textarea> (value, onChange,
 *           placeholder, type, disabled, onKeyDown, rows, inputMode, etc.)
 */
const TextInput = ({
  label,
  helperText,
  error = false,
  leftIcon,
  fullWidth = true,
  multiline = false,
  className = "",
  id,
  ...rest
}) => {
  const autoId = useId();
  const inputId = id || autoId;
  const Field = multiline ? "textarea" : "input";

  return (
    <div
      className={`gx-field ${fullWidth ? "is-full" : ""} ${error ? "is-error" : ""} ${className}`}
    >
      {label && (
        <label htmlFor={inputId} className="gx-field-label">
          {label}
        </label>
      )}
      <div className="gx-field-wrap">
        {leftIcon && <span className="gx-field-icon">{leftIcon}</span>}
        <Field
          id={inputId}
          className={`gx-field-input ${leftIcon ? "has-icon" : ""} ${multiline ? "is-multiline" : ""}`}
          aria-invalid={error || undefined}
          {...rest}
        />
      </div>
      {helperText && <div className="gx-field-help">{helperText}</div>}
    </div>
  );
};

export default TextInput;
